import React, { FC, memo } from 'react';
import dynamic from 'next/dynamic';
import Grid, { GridSpacing } from '@material-ui/core/Grid';

import { Section } from '@keaze/web/components/section';
import { CardGroupProps } from '@keaze/web/components/cardGroup';
import { Map, OnMapClick } from './map';
import {
  LocationContainer,
  Content,
  Carousel,
  ItemMap,
} from './listingDetails.styles';
import { useListingDetails } from './listingDetails.hook';
import {
  ListingDetailsProps,
  newListingDetailProps,
} from './listingDetails.types';
import { OnRegisterButtonClick } from './listingDetails.types';
import { Property } from './property';
import { SingleProperty } from './singleProperty';
import { Header } from './header';
import { MapModalProps } from './mapModal';
import { RegisterInterestDialogProps } from './registerInterestDialog';
import { Theme, useMediaQuery } from '@material-ui/core';
import { ListingImage } from './utils/listingImages';
import { Schemes } from './header/header.styles';

import {
  DesktopStartRegistration,
  SinglePropertyBreakdown,
} from './singleProperty/singleProperty.styles';

const DynamicCardGroup = dynamic<CardGroupProps>(() =>
  import('@keaze/web/components/cardGroup').then((model) => model.CardGroup)
);

const DynamicMapModal = dynamic<MapModalProps>(
  () => import('./mapModal').then((model) => model.MapModal),
  {
    ssr: false,
  }
);

const DynamicRegisterInterestDialog = dynamic<RegisterInterestDialogProps>(
  () =>
    import('./registerInterestDialog').then(
      (model) => model.RegisterInterestDialog
    ),
  {
    ssr: false,
  }
);

const DEFAULT_SPACING: GridSpacing = 3;

type RenderLocationSectionArgs = {
  isDownMd: boolean;
  spacing: GridSpacing;
  listingImages: ListingImage[];
  mapImageUrl: string;
  onMapClick: OnMapClick;
};

type RenderLocationSection = (
  args: RenderLocationSectionArgs & ListingDetailsProps & newListingDetailProps
) => JSX.Element;

const renderLocationSection: RenderLocationSection = ({
  listing,
  schemes,
  isLoading,
  isListingLoading,
  isDownMd,
  spacing,
  listingImages,
  mapImageUrl,
  onRegisterButtonClick,
  onMapClick,
}) => (
  <LocationContainer container spacing={spacing} wrap="nowrap">
    <Grid item sm={12} md={8}>
      <Carousel schemes={schemes} data={listingImages} />
    </Grid>

    <ItemMap item sm={12} md={4}>
      <Map mapImageUrl={mapImageUrl} onClick={onMapClick} />

      <DesktopStartRegistration
        listing={listing}
        isLoading={isLoading}
        isListingLoading={isListingLoading}
        onRegisterButtonClick={onRegisterButtonClick}
      />
    </ItemMap>
  </LocationContainer>
);

export const Listing: FC<ListingDetailsProps & newListingDetailProps> = memo(
  ({ listing, listingImages, properties, similar, schemes, isLoading }) => {
    const {
      registerInterestOpen,
      toggleRegisterInterestDialog,
      address,
      listingData,
      similarData,
      isRegisterInterestLoading,
      isListingLoading,
      onMapClick,
      onVideoClick,
      onRegisterButtonClick,
    } = useListingDetails(listing, similar);
    const isDownMd = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down('sm')
    );

    return (
      <Section position="topRight">
        <Header
          name={listingData.name}
          address={address}
          newBuild={listingData.newBuild}
          schemes={listingData.schemes}
          availabilityStatus={listingData.availabilityStatus}
          listingId={listingData.id}
          listingSlug={listingData.slug}
          favourite={listingData.favourite}
          isListingLoading={isListingLoading}
          onMapClick={onMapClick}
        />
        {renderLocationSection({
          isDownMd,
          spacing: DEFAULT_SPACING,
          listingImages,
          mapImageUrl: listingData.mapImageUrl,
          onMapClick,
          schemes: listingData.schemes,
          properties: [],
          similar: [],
          listing: listingData,
          isLoading: isListingLoading,
          isListingLoading: isListingLoading,
          onRegisterButtonClick: onRegisterButtonClick,
        })}

        <Content>
          {listingData.singleProperty ? (
            <SingleProperty
              listing={listingData}
              address={address}
              spacing={DEFAULT_SPACING}
              isRegisterInterestLoading={isRegisterInterestLoading}
              isListingLoading={isListingLoading}
              onRegisterButtonClick={onRegisterButtonClick}
              onVideoClick={onVideoClick}
            />
          ) : (
            <Property
              listing={listingData}
              properties={properties}
              spacing={DEFAULT_SPACING}
              address={address}
              isRegisterInterestLoading={isRegisterInterestLoading}
              isListingLoading={isListingLoading}
              onVideoClick={onVideoClick}
              onRegisterButtonClick={onRegisterButtonClick}
            />
          )}
        </Content>

        {/* {similarData.length !== 0 && (
          <DynamicCardGroup
            title="Similar properties"
            data={similarData}
            GridItemProps={{ xs: 12, sm: 6, md: 4 }}
            withFavorite
          />
        )} */}
        <DynamicMapModal listing={listing} description={address} />
        <DynamicRegisterInterestDialog
          listing={listing}
          open={registerInterestOpen}
          onClose={toggleRegisterInterestDialog}
        />
      </Section>
    );
  }
);
